import { styled, useTheme } from '@mui/material';
import { useState } from 'react';
import { useLoadingBar } from '../../hooks/useLoadingBar';
import { useToastMessage } from '../../hooks/useToastMessage';
import { extractErrorMessage } from '../../utils/errorNotifier';
import {
  BULK_APPROVE_CHUNK_SIZE,
  useBulkApproveMontlyTimesheet,
} from '../../queries/hooks/useBulkApproveMontlyTimesheet';
import { LoadingId, MonthlyTimesheet, TimesheetStatus } from '../../types';
import { getMonthLabel } from '../../utils/formatters';
import { ModalComponent } from '../ModalComponent/ModalComponent';
import { Typography } from '../Typography/Typography';
import { ProgressBar } from '../ProgressBar/ProgressBar';

type Props = {
  timesheets: MonthlyTimesheet[];
  toggleOpen: () => void;
  resetSelection: () => void;
  isOpen: boolean;
  approveStatus: TimesheetStatus;
  // Optional per-row target status. When provided, selected timesheets are grouped by their target
  // status and each group is approved with its own status (SVP-stage rows close, Manager-stage rows
  // move to Deal Owners) instead of everything sharing `approveStatus`.
  resolveApproveStatus?: (timesheetId: number) => TimesheetStatus;
  sendToLabel?: string;
  onApprove: () => void;
};

export function BulkApproveTimesheetModal({
  timesheets,
  isOpen,
  toggleOpen,
  resetSelection,
  approveStatus,
  resolveApproveStatus,
  sendToLabel,
  onApprove,
}: Props) {
  const { colors } = useTheme();

  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();
  const title = `Approve ${timesheets?.length} Timesheets`;

  const selectedTsIds = timesheets.map((ts) => Number(ts.id));

  const modalBodyMsg = (() => {
    let message = '';
    timesheets.forEach((ts, index) => {
      const month = getMonthLabel(ts.month);
      const hasAnd = timesheets[index] !== timesheets[timesheets.length - 1];
      message += `${ts.user?.name} ${month} ${ts.year}`;
      message += hasAnd ? ` and ` : '';
    });

    return message;
  })();

  const { mutateAsync: approveTimesheets, isLoading: approveInProgress } =
    useBulkApproveMontlyTimesheet();

  // Group selected ids by their target status. Without a resolver everything shares `approveStatus`
  // (unchanged behavior); with one, SVP-stage and Manager-stage rows form separate groups.
  const approvalGroups = (() => {
    const groups = new Map<TimesheetStatus, number[]>();
    selectedTsIds.forEach((id) => {
      const status = resolveApproveStatus ? resolveApproveStatus(id) : approveStatus;
      groups.set(status, [...(groups.get(status) ?? []), id]);
    });
    return groups;
  })();

  const totalBatches = Array.from(approvalGroups.values()).reduce(
    (acc, ids) => acc + Math.ceil(ids.length / BULK_APPROVE_CHUNK_SIZE),
    0
  );
  const showProgress = totalBatches > 1;
  const [progress, setProgress] = useState(0);

  const handleApproveTimesheet = async () => {
    startLoading(LoadingId.bulkApproveTimesheet);
    setProgress(0);
    try {
      let completedBatchesBase = 0;
      // Approve each status group sequentially; progress is cumulative across all groups.
      for (const [status, ids] of approvalGroups) {
        await approveTimesheets({
          ids,
          status,
          onProgress: (completedBatches) => {
            setProgress(totalBatches ? Math.round(((completedBatchesBase + completedBatches) / totalBatches) * 100) : 100);
          },
        });
        completedBatchesBase += Math.ceil(ids.length / BULK_APPROVE_CHUNK_SIZE);
      }
      resetSelection();
      pushSuccessToast({
        title: 'Timesheets Approved',
        message: `${modalBodyMsg} timesheets has been approved ${sendToLabel ? `and sent to ${sendToLabel}` : ''}.`,
      });
      toggleOpen();
      onApprove();
    } catch (error: unknown) {
      pushErrorToast({ message: extractErrorMessage(error) });
    } finally {
      stopLoading(LoadingId.bulkApproveTimesheet);
    }
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={toggleOpen}
      actionButtonsProps={{
        isSubmitDisabled: approveInProgress,
        onSubmit: handleApproveTimesheet,
        onCancel: toggleOpen,
        submitText: `Approve`,
        submitBtnStyle: {},
      }}
      title={title}
      top='80px'
    >
      {showProgress && approveInProgress ? (
        <ProgressWrapper>
          <Typography variant='body' color={colors.text.main}>
            Please wait, this can take some time. Approving your timesheets…
          </Typography>
          <ProgressBarWrapper>
            <ProgressBar progress={progress} color={colors.surfaceAccent.default} height='8px' />
          </ProgressBarWrapper>
          <Typography variant='caption' color={colors.text.secondary}>
            {`${progress}%`}
          </Typography>
        </ProgressWrapper>
      ) : (
        <InfoWrapper>
          <Typography variant='body' color={colors.text.main}>
            {`Are you sure you want to approve ${modalBodyMsg} timesheets?`}
          </Typography>
        </InfoWrapper>
      )}
    </ModalComponent>
  );
}

const InfoWrapper = styled('div')`
  border-radius: 2px;
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;

const ProgressWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
`;

const ProgressBarWrapper = styled('div')`
  border-radius: 4px;
  overflow: hidden;
`;
